/**
 * The MEMBER-facing loop human-wait RPC definitions (OL-369).
 *
 * The signed-in member's twin of `admin/orcaloops-loop-waits.rpc.ts`: list the
 * caller's customer's open loop human waits, answer one. The wire schemas —
 * {@link LoopHumanWaitInboxRow}, {@link AnswerLoopHumanWaitResult} and
 * {@link LoopHumanWaitNotAnswerableError} — are the admin group's, imported and
 * never redeclared.
 *
 * **A payload `customerId` is only a SELECTOR among the caller's memberships —
 * never an authority.** The handlers resolve the customer from the actor through
 * `resolveActorCustomer` (`loop-scope.ts`), which is why both rpcs declare
 * {@link ValidationError}: a caller with several memberships and no selector gets
 * one naming the candidates. Every rpc carries {@link LoopActorMiddleware}.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import {
  AnswerLoopHumanWaitResult,
  LoopHumanWaitInboxRow,
  LoopHumanWaitNotAnswerableError,
} from "../admin/orcaloops-loop-waits.rpc.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * Read the open waits of the caller's customer. `customerId` selects among the
 * caller's memberships; omit it when the caller has exactly one (or a pinned
 * token).
 *
 * @since 1.0.0
 */
export const LoopHumanWaitListInput = Schema.Struct({
  customerId: Schema.optional(Schema.String),
});
export type LoopHumanWaitListInput = Schema.Schema.Type<typeof LoopHumanWaitListInput>;

/**
 * Answer one open wait of the caller's customer. `customerId` is a selector,
 * exactly as on {@link LoopHumanWaitListInput}.
 *
 * @since 1.0.0
 */
export const LoopHumanWaitAnswerInput = Schema.Struct({
  customerId: Schema.optional(Schema.String),
  waitId: Schema.String,
  text: Schema.String,
  option: Schema.optional(Schema.String),
});
export type LoopHumanWaitAnswerInput = Schema.Schema.Type<typeof LoopHumanWaitAnswerInput>;

/**
 * Member loop human-wait RPC group.
 *
 * @since 1.0.0
 */
export class LoopHumanWaitRpcs extends RpcGroup.make(
  Rpc.make("loops.humanWaits.list", {
    payload: LoopHumanWaitListInput,
    success: Schema.Array(LoopHumanWaitInboxRow),
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.humanWaits.answer", {
    payload: LoopHumanWaitAnswerInput,
    success: AnswerLoopHumanWaitResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      RecordNotFoundError,
      LoopHumanWaitNotAnswerableError,
      DatabaseError,
    ),
  }).middleware(LoopActorMiddleware),
) {}
